import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { SkillListAction } from "../../../../Redux/Actions/CareerAction";

export interface JobCreate {
  title: string;
  meta_data: string;
  company_description: string;
  role_description: string;
  required_skills: number[];
  requirements: string;
  responsibilities: string;
  qualifications: string;
  benefits: string | null;
  location: string;
  salary: string;
  start_date: string;
  end_date: string | null;
  vacancies: string;
  industry: string;
  job_type: string;
  workplace_type: string;
}

interface CreateJobFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: JobCreate) => void;
}

interface Skill {
  id: number;
  name: string;
}

interface FormValues {
  title: string;
  meta_data: string;
  company_description: string;
  role_description: string;
  required_skills: number[];
  requirements: string;
  responsibilities: string;
  qualifications: string;
  benefits: string;
  location: string;
  salary: string;
  start_date: string;
  end_date: string | null;
  vacancies: string;
  industry: string;
  job_type: string;
  workplace_type: string;
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Job title is required"),
  meta_data: Yup.string().required("Summary is required"),
  company_description: Yup.string().required("Company description is required"),
  role_description: Yup.string().required("Role description is required"),
  required_skills: Yup.array()
    .of(Yup.number())
    .min(1, "Select at least one skill"),
  requirements: Yup.string().required("Requirements are required"),
  responsibilities: Yup.string().required("Responsibilities are required"),
  qualifications: Yup.string().required("Qualifications are required"),
  benefits: Yup.string().nullable(),
  location: Yup.string().required("Location is required"),
  salary: Yup.string().required("Salary is required"),
  start_date: Yup.date()
    .required("Start date is required")
    .typeError("Invalid date"),
  end_date: Yup.date().nullable().typeError("Invalid date"),
  vacancies: Yup.string().required("Vacancies are required"),
  industry: Yup.string().required("Industry is required"),
  job_type: Yup.string().required("Job type is required"),
  workplace_type: Yup.string().required("Workplace type is required"),
});

const CreateJob: React.FC<CreateJobFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch<any>();
  const skills: Skill[] = useSelector(
    (state: any) => state.SkillListData.data || []
  );
  const CreateJobResponse = useSelector((state: any) => state.JobCreateData);

  useEffect(() => {
    if (CreateJobResponse?.status) {
      setValues(initialForm);
      onClose();
    }
  }, [CreateJobResponse]);

  const initialForm: FormValues = {
    title: "",
    meta_data: "",
    company_description: "",
    role_description: "",
    required_skills: [],
    requirements: "",
    responsibilities: "",
    qualifications: "",
    benefits: "",
    location: "",
    salary: "",
    start_date: "",
    end_date: null,
    vacancies: "",
    industry: "",
    job_type: "",
    workplace_type: "",
  };

  const [values, setValues] = useState<FormValues>(initialForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormValues, string>>
  >({});

  useEffect(() => {
    dispatch(SkillListAction(""));
  }, [dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & {
      name?: string;
    }
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const valid = await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      const payload: JobCreate = {
        title: valid.title,
        meta_data: valid.meta_data,
        company_description: valid.company_description,
        role_description: valid.role_description,
        required_skills: (valid.required_skills || []).filter(
          (skill): skill is number => skill !== undefined
        ),
        requirements: valid.requirements,
        responsibilities: valid.responsibilities,
        qualifications: valid.qualifications,
        benefits: valid.benefits || null,
        location: valid.location,
        salary: valid.salary,
        start_date:
          valid.start_date instanceof Date
            ? valid.start_date.toISOString()
            : valid.start_date,
        end_date: valid.end_date
          ? valid.end_date instanceof Date
            ? valid.end_date.toISOString()
            : valid.end_date
          : null,
        vacancies: valid.vacancies,
        industry: valid.industry,
        job_type: valid.job_type,
        workplace_type: valid.workplace_type,
      };
      onSubmit(payload);
    } catch (err: any) {
      const formErrors: any = {};
      if (err.inner) {
        err.inner.forEach((validationError: any) => {
          formErrors[validationError.path as keyof FormValues] =
            validationError.message;
        });
      }
      setErrors(formErrors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Job</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="title"
            label="Job Title"
            value={values.title}
            onChange={handleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
          />
          <TextField
            name="meta_data"
            label="Summary"
            value={values.meta_data}
            onChange={handleChange}
            error={Boolean(errors.meta_data)}
            helperText={errors.meta_data}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            name="company_description"
            label="Company Description"
            value={values.company_description}
            onChange={handleChange}
            error={Boolean(errors.company_description)}
            helperText={errors.company_description}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            name="role_description"
            label="Role Description"
            value={values.role_description}
            onChange={handleChange}
            error={Boolean(errors.role_description)}
            helperText={errors.role_description}
            fullWidth
            multiline
            rows={3}
          />
          <Autocomplete
            multiple
            options={skills}
            getOptionLabel={(option) => option.name}
            value={skills.filter((s) => values.required_skills.includes(s.id))}
            onChange={(e, newValue) =>
              setValues((prev) => ({
                ...prev,
                required_skills: newValue.map((v) => v.id),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Required Skills"
                error={Boolean(errors.required_skills)}
                helperText={errors.required_skills}
                fullWidth
              />
            )}
          />
          <TextField
            name="requirements"
            label="Requirements"
            value={values.requirements}
            onChange={handleChange}
            error={Boolean(errors.requirements)}
            helperText={errors.requirements}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            name="responsibilities"
            label="Responsibilities"
            value={values.responsibilities}
            onChange={handleChange}
            error={Boolean(errors.responsibilities)}
            helperText={errors.responsibilities}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            name="qualifications"
            label="Qualifications"
            value={values.qualifications}
            onChange={handleChange}
            error={Boolean(errors.qualifications)}
            helperText={errors.qualifications}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            name="benefits"
            label="Benefits"
            value={values.benefits}
            onChange={handleChange}
            error={Boolean(errors.benefits)}
            helperText={errors.benefits}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            name="location"
            label="Location"
            value={values.location}
            onChange={handleChange}
            error={Boolean(errors.location)}
            helperText={errors.location}
            fullWidth
          />
          <TextField
            name="salary"
            label="Salary"
            value={values.salary}
            onChange={handleChange}
            error={Boolean(errors.salary)}
            helperText={errors.salary}
            fullWidth
          />
          <TextField
            name="start_date"
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={values.start_date}
            onChange={handleChange}
            error={Boolean(errors.start_date)}
            helperText={errors.start_date}
            fullWidth
          />
          <TextField
            name="end_date"
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={values.end_date}
            onChange={handleChange}
            error={Boolean(errors.end_date)}
            helperText={errors.end_date}
            fullWidth
          />
          <TextField
            name="vacancies"
            label="Vacancies"
            value={values.vacancies}
            onChange={handleChange}
            error={Boolean(errors.vacancies)}
            helperText={errors.vacancies}
            fullWidth
          />
          <TextField
            name="industry"
            label="Industry"
            value={values.industry}
            onChange={handleChange}
            error={Boolean(errors.industry)}
            helperText={errors.industry}
            fullWidth
          />
          <TextField
            name="job_type"
            label="Job Type"
            value={values.job_type}
            onChange={handleChange}
            error={Boolean(errors.job_type)}
            helperText={errors.job_type}
            fullWidth
          />
          <TextField
            name="workplace_type"
            label="Workplace Type"
            value={values.workplace_type}
            onChange={handleChange}
            error={Boolean(errors.workplace_type)}
            helperText={errors.workplace_type}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Job
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateJob;
